/**
 * @fileoverview Base class for viewer components with state persistence.
 * @module ui/StatefulViewer
 */


/**
 * Base class for viewer components that need state persistence via StateManager.
 * Provides common patterns for modal handling, state access, and blur handling.
 *
 * Extended by:
 * - SettingsViewer
 * - HelpViewer
 *
 * @class
 */
export class StatefulViewer {
    /**
     * Creates a new StatefulViewer instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {string} containerId - ID of the container element
     */
    constructor(mapApp, containerId) {
        this.mapApp = mapApp;
        this.container = document.getElementById(containerId);
    }


    /**
     * Gets the StateManager instance from mapApp.
     * @returns {Object} The StateManager instance
     */
    getStateManager() {
        return this.mapApp.getStateManager();
    }


    /**
     * Sets up the blur handler for a Bootstrap modal to avoid aria-hidden warnings.
     * Only adds the listener once (checks for marker attribute).
     * @param {string} modalId - The ID of the modal element
     * @protected
     */
    setupModalBlurHandler(modalId) {
        const modalElement = document.getElementById(modalId);
        if (!modalElement) return;

        // Only add listener once (check for marker attribute)
        if (modalElement.dataset.blurListenerAdded) return;

        modalElement.addEventListener('hide.bs.modal', () => {
            if (document.activeElement && modalElement.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        });
        modalElement.dataset.blurListenerAdded = 'true';
    }


    /**
     * Shows a Bootstrap modal by ID with blur handling.
     * @param {string} modalId - The ID of the modal element
     * @returns {Object} The Bootstrap Modal instance
     * @protected
     */
    showModalById(modalId) {
        this.setupModalBlurHandler(modalId);
        const modalElement = document.getElementById(modalId);
        const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
        modal.show();
        return modal;
    }


    /**
     * Hides a Bootstrap modal by ID.
     * @param {string} modalId - The ID of the modal element
     * @returns {boolean} True if modal was hidden, false if not found
     * @protected
     */
    hideModalById(modalId) {
        const modalElement = document.getElementById(modalId);
        if (modalElement) {
            const modal = bootstrap.Modal.getInstance(modalElement);
            if (modal) {
                modal.hide();
                return true;
            }
        }
        return false;
    }
}
